import { bucketSlot } from "./context-bandit";

/**
 * Pull the copy-variant id out of a tracking `extra` payload.
 *
 * `AffiliateImpression` and `AffiliateLink` both take an open
 * `extra: Record<string, unknown>` that callers spread into the PostHog event,
 * and `OfferCard` puts `variant_id` in it. The D1 beacon takes typed fields
 * instead, so this is the one place the loosely-typed property is narrowed
 * before it reaches a column.
 *
 * Dependency-free and its own module on purpose: both callers are client
 * components, and importing `copy-variants.ts` for this would drag zod into
 * their bundle for a five-line read.
 */
export function readVariantId(
	extra: Record<string, unknown> | undefined,
): string | undefined {
	const value = extra?.variant_id;
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Same alphabet as `copyVariantSchema.id` (kept zod-free for the client). */
const VARIANT_ID_SHAPE = /^[A-Za-z0-9_.:-]{1,64}$/;

/**
 * The go-route's `v` param: the copy-variant id of the card that was clicked,
 * or undefined when absent or off-shape. Without it every engine click the
 * route writes lands with `variant_id = NULL`, crediting every copy variant's
 * clicks to control.
 */
export function parseVariantParam(raw: string | null): string | undefined {
	return raw && VARIANT_ID_SHAPE.test(raw) ? raw : undefined;
}

/**
 * D1 `slot` value for an affiliate impression/click: `cb:<bucket>` from the
 * `context_bucket` the decision trace put in `extra`, so the contextual
 * bandit can count per (surface, bucket) without a schema change. Anything
 * outside the closed bucket vocabulary is dropped.
 */
export function readContextBucketSlot(
	extra: Record<string, unknown> | undefined,
): string | undefined {
	const value = extra?.context_bucket;
	return typeof value === "string" ? bucketSlot(value) : undefined;
}

/** The bare bucket key from `extra` (for the go-route's `cb` param). */
export function readContextBucket(
	extra: Record<string, unknown> | undefined,
): string | undefined {
	return readContextBucketSlot(extra)
		? (extra?.context_bucket as string)
		: undefined;
}
